import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

const REFERRAL_STORAGE_KEY = 'accommodation-referral';
const GCLID_URL_KEY = 'gclid';
const LABEL_URL_KEY = 'label';

const timeInSeconds = () => Math.floor(Date.now() / 1000);

const createStoredId = (referral: string): string => {
    const thirtyDaysAheadInSeconds = timeInSeconds() + 60 * 60 * 24 * 30;
    const json = JSON.stringify({ referral, expires: thirtyDaysAheadInSeconds });
    return encodeURIComponent(json);
};

const readStoredId = (storageKey: string, key: string, jsonMap?: string): string | undefined => {
    if (jsonMap) {
        const storedObject = JSON.parse(jsonMap);
        if (storedObject?.expires && storedObject.expires > timeInSeconds()) {
            return storedObject[key];
        }
        window.localStorage.removeItem(storageKey);
    }
    return undefined;
};

const addToLocalStorage = (urlKey: string): void => {
    try {
        const id = window.location.search
            ?.substr(1)
            .split('&')
            .filter((s) => s.startsWith(`${urlKey}=`))
            .map((s) => s.split('=')[1])[0];
        if (id) {
            window.localStorage.setItem(REFERRAL_STORAGE_KEY, createStoredId(id));
        }
    } catch (e) {
        // do nothing
    }
};

const getFromLocalStorage = (storageKey: string, key: string): string | undefined => {
    const json = decodeURIComponent(window.localStorage.getItem(storageKey) || '');
    return readStoredId(storageKey, key, json);
};

// TODO: Remove gclid as json key when last bad json has expired, on 05.10.22
export const getReferralFromLocalStorage = (): string | undefined =>
    getFromLocalStorage(REFERRAL_STORAGE_KEY, 'referral') ?? getFromLocalStorage(REFERRAL_STORAGE_KEY, 'gclid');

interface LocalStorage {
    referral?: string;
}

const LocalStorageContext = createContext<LocalStorage>(undefined);

export const LocalStorageProvider = ({ children }: { children: ReactNode }) => {
    const [localStorage, setLocalStorage] = useState<LocalStorage>({});
    useEffect(() => {
        addToLocalStorage(GCLID_URL_KEY);
        addToLocalStorage(LABEL_URL_KEY);
        setLocalStorage({
            referral: getReferralFromLocalStorage(),
        });
    }, []);
    return <LocalStorageContext.Provider value={localStorage}>{children}</LocalStorageContext.Provider>;
};

export const useLocalStorage = (): LocalStorage => useContext(LocalStorageContext);
